/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { useParams, } from "react-router-dom";
import { useMutation, useQuery, } from "@tanstack/react-query";
import {
  IRepaymentScheduleByIdResponse,
  limitPaymentScheduleByIdQueryConfig,
  paymentLinkMutationFn,
} from "../../../../api";
import {
  asPaymentSplit,
  EPaymentSplit,
  getAllUnpaidPayments,
  hasUpcomingPayment,
  shouldBePaid,
} from "../../OperationPage/PaymentList/utils";
import { Box, Button, Card, CardBody, Heading, HStack, Skeleton, SkeletonCircle, Stack, Text, } from "@chakra-ui/react";
import { Indicator, } from "../../OperationPage/PaymentList/Indicator";
import { formatDate, } from "../../../../utils/formatDate.ts";
import { formatPrice, } from "../../../../utils/formatPrice.ts";
import { PlaceholderCard, } from "../../../shared/PlaceholderCard";
import { usePopupWindow, } from "../../../../utils/hooks";

const indicatorVariantMap: Record<number, "default" | "success" | "danger"> = {
  1: "default",
  2: "success",
  3: "danger",
};

export function PaymentList() {
  const limitId = useParams().id!;
  const paymentSplitQuery = useQuery({
    ...limitPaymentScheduleByIdQueryConfig(limitId,),
    select: ({ items, ...totals },) => {
      const statusMap = {
        PLANNED: 1,
        PAID: 2,
        OVERDUE: 3,
        PARTIALLY_PAID: 1,
      };
      const preTransform: IRepaymentScheduleByIdResponse[] = items.map(payment => ({
        number: payment.number,
        date: payment.date,
        amount: payment.remainingPaymentAmount,
        currencyCode: "RUB",
        status: statusMap[payment.status as keyof typeof statusMap],
        actualPaymentAmount: (payment.plannedPaymentAmount - payment.remainingPaymentAmount),
      }),);
      return {
        split: asPaymentSplit(preTransform,),
        isEmpty: items.length === 0,
        total: totals,
      };
    },
  },);
  const { openPopup, setUrl, } = usePopupWindow();
  const mutation = useMutation({
    mutationFn: paymentLinkMutationFn,
    onSuccess: (data,) => {
      setUrl(data.redirectUrl,);
    },
    onError: (e,) => {
      console.log("Error", e,);
    },
  },);

  const handlePayNow = (part: IRepaymentScheduleByIdResponse[],) => () => {
    const sum = part
      .map(p => p.amount,)
      .reduce((acc, num,) => acc + num, 0,);
    openPopup();
    mutation.mutate({ limitId, amount: sum, },);
  };

  const handlePayAll = () => {
    if (paymentSplitQuery.isSuccess) {
      const sum = getAllUnpaidPayments(paymentSplitQuery.data.split,)
        .map(i => i.amount,)
        .reduce((acc, num,) => acc + num, 0,);
      openPopup();
      mutation.mutate({ limitId, amount: sum, },);
    } else {
      // this is the corner case it should be reported in the monitoring system
      console.error("Schedule is not loaded",);
    }
  };

  const unpaidIndexMap = new Map<number, number>();
  let counter = 1;

  if (paymentSplitQuery.isSuccess) {

    paymentSplitQuery.data.split.forEach((part,) => {
      part.forEach((payment,) => {
        unpaidIndexMap.set(payment.number, counter,);
        counter += 1;
      },);
    },);
  }

  return (
    <Box>
      <Box>
        <Skeleton isLoaded={!paymentSplitQuery.isLoading}>
          {paymentSplitQuery.isSuccess && !paymentSplitQuery.data.isEmpty && (
            <Box pt={6} pb={7}>
              <Heading fontSize="xl" mb="3">
                График платежей
              </Heading>
              <Text>
                Осталось оплатить
                {" "}
                {formatPrice((paymentSplitQuery.data.total.totalRemainingPaymentAmount / 100), 0,)}
              </Text>
            </Box>
          )}
        </Skeleton>
      </Box>
      {paymentSplitQuery.isLoading && (
        <Stack spacing={3} py={4}>
          {new Array(4,).fill(0,).map((_, i,) => (
            <HStack key={i} spacing={4} py={2}>
              <SkeletonCircle boxSize="38px" />
              <Skeleton w="50%" h={6} />
              <Box flex="1" />
              <Skeleton w="20%" h={6} />
            </HStack>
          ),)}
        </Stack>
      )}
      <Stack spacing={3}>
        {paymentSplitQuery.isSuccess && paymentSplitQuery.data.split.map((part, spiltType,) =>
          part.length > 0 && (
            <Card
              key={spiltType}
              variant={shouldBePaid(spiltType,) ? "elevated" : "unstyled"}
            >
              <CardBody p={3}>
                <Stack spacing={3}>
                  {part.map((payment,) => (
                    <Box
                      key={payment.number}
                      py={1}
                      color={EPaymentSplit.Paid === spiltType ? "gray.500" : undefined}
                    >
                      <HStack spacing={4}>
                        <Indicator
                          variant={indicatorVariantMap[payment.status]}
                          text={
                            payment.status !== 2
                              ? unpaidIndexMap.get(payment.number,)?.toString()
                              : undefined
                          }
                        />
                        <Box flex="1">
                          <Text>{formatDate(new Date(payment.date,),)}</Text>
                          {payment.status === 3 && (
                            <Text fontSize="sm" color="red.500">Просрочен</Text>
                          )}
                        </Box>
                        <Box>
                          <Text>{formatPrice(payment.amount / 100, 0,)}</Text>
                        </Box>
                      </HStack>
                    </Box>
                  ),)}
                </Stack>
                {shouldBePaid(spiltType,) && (
                  <Button mt={3} colorScheme="brand" w="100%" onClick={handlePayNow(part,)}>
                    {spiltType === EPaymentSplit.FstUpcoming && "Оплатить сейчас"}
                    {spiltType === EPaymentSplit.Overdue && "Оплатить долг"}
                  </Button>
                )}
              </CardBody>
            </Card>
          )
          ,)}
        {paymentSplitQuery.isSuccess && paymentSplitQuery.data.isEmpty && (
          <PlaceholderCard
            size="lg"
            title="У вас пока нет покупок и активных графиков"
          />
        )}
      </Stack>
      {paymentSplitQuery.isSuccess && hasUpcomingPayment(paymentSplitQuery.data.split,) && (
        <Box pt={4}>
          <Button colorScheme="black" w="100%" onClick={handlePayAll}>
            Погасить полностью
          </Button>
        </Box>
      )}
    </Box>
  );
}
